import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Trash2 } from "lucide-react";
import { useWishlist } from "../lib/wishlist";
import { useSEO } from "../lib/seo";
import BlurHashImage from "../components/BlurHashImage";

export default function Wishlist() {
  const { items, removeWishlist, clearWishlist } = useWishlist();
  const [confirmClear, setConfirmClear] = useState(false);

  useSEO({
    title: "Wishlist — PrintMyTees",
    description: "Your saved PrintMyTees products and designs.",
  });

  return (
    <div className="container-pmt py-10 sm:py-12" data-testid="wishlist-page">
      <div className="overline">Saved for later</div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="display-h2 mt-2">My Wishlist</h1>
          <p className="mt-3 text-stone-600">
            Save products and designs you want to come back to.
          </p>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-3 self-start sm:self-auto">
            {confirmClear ? (
              <>
                <span className="text-xs text-stone-600">Clear all saved items?</span>
                <button
                  type="button"
                  onClick={() => {
                    clearWishlist();
                    setConfirmClear(false);
                  }}
                  className="text-xs font-semibold text-red-700 underline"
                  data-testid="wishlist-clear-confirm"
                >
                  Yes, clear
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmClear(false)}
                  className="text-xs underline text-stone-500"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmClear(true)}
                className="text-xs underline text-stone-500 hover:text-[#D95328]"
                data-testid="wishlist-clear"
              >
                Clear wishlist
              </button>
            )}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="mt-8 sm:mt-10 border border-[#E6DFD5] bg-[#F3EDE2] p-6 sm:p-10 text-center">
          <Heart className="mx-auto text-stone-400" size={34} />
          <div className="mt-4 font-bold uppercase text-lg">Your wishlist is empty</div>
          <p className="mt-2 text-sm text-stone-600">
            Nothing saved yet? Explore our designs or shop products and tap ♡ to save your favourites.
          </p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Link to="/designs" className="btn-primary">Browse Designs</Link>
            <Link to="/shop" className="btn-outline-pmt">Shop Products</Link>
          </div>
        </div>
      ) : (
        <div className="mt-7 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {items.map((item) => {
            const href =
              item.type === "design"
                ? `/products?design=${encodeURIComponent(item.id)}`
                : `/shop/${encodeURIComponent(item.id)}`;

            return (
              <div key={item.key} className="card-pmt group relative overflow-hidden">
                <Link
                  to={href}
                  className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-inset"
                >
                  <div className="aspect-[4/5] bg-[#F3EDE2] overflow-hidden">
                    {item.image ? (
                      <BlurHashImage
                        src={item.image}
                        alt={item.name || "Saved item"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-400">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="p-3 sm:p-4 min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500 truncate">
                      {item.type === "design"
                        ? item.category || item.group || "Design"
                        : "Product"}
                    </div>
                    <div
                      className="mt-1 font-bold uppercase break-words"
                      style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                    >
                      {item.name}
                    </div>
                    {item.price != null && (
                      <div className="mt-2 text-[#D95328] font-mono">
                        ₹{Number(item.price).toLocaleString("en-IN")}
                      </div>
                    )}
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => removeWishlist(item.type, item.id)}
                  className="absolute top-2.5 right-2.5 w-10 h-10 rounded-full bg-white/95 border border-[#E6DFD5] flex items-center justify-center text-stone-700 hover:text-[#D95328] shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328]"
                  aria-label={`Remove ${item.name || "item"} from wishlist`}
                  title="Remove from wishlist"
                  data-testid={`wishlist-remove-${item.id}`}
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-8 text-center">
          <Link to="/cart" className="btn-outline-pmt inline-flex">
            <ShoppingBag size={16} /> Go to Cart
          </Link>
        </div>
      )}
    </div>
  );
}
